/*
 * <heritage-gallery-lightbox>
 *
 * Heritage gallery lightbox / zoom viewer. Opens a fullscreen overlay
 * showing a single image at full resolution with:
 *   - Wheel / pinch zoom (1x – 4x)
 *   - Drag to pan when zoomed in
 *   - Arrow-key + on-screen prev/next navigation between images
 *   - Thumbnail strip for direct navigation
 *   - Optional native Fullscreen API toggle
 *
 * Listens on document for `mdj:gallery-zoom` with detail:
 *   {
 *     images:     Array<string>  — image URLs in display order
 *     startIndex: number          — index of the image to open with
 *     fullscreen: boolean         — request native fullscreen on open
 *   }
 *
 * Closes on ESC, backdrop click, or X button. Body scroll is locked
 * while open via the .heritage-overlay-open class on <html>.
 */

class HeritageGalleryLightbox extends HTMLElement {
  constructor() {
    super();
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleZoomEvent = this.handleZoomEvent.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);

    this.images = [];
    this.currentIndex = 0;
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isPanning = false;
    this.panStart = { x: 0, y: 0, tx: 0, ty: 0 };
    this.pinch = null;
  }

  connectedCallback() {
    if (this._initialized) return;

    // Portal to document.body so the fixed-position overlay escapes
    // the parent .shopify-section's stacking context (z-index: 2),
    // which would otherwise pin the lightbox under the header
    // (.shopify-section-group-header-group sits at z-index: 100 in
    // base.css). After re-parenting, connectedCallback fires again
    // and the guard below lets the real init run.
    if (document.body && this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._initialized = true;

    this.imageEl    = this.querySelector('[data-lightbox-image]');
    this.thumbsEl   = this.querySelector('[data-lightbox-thumbs]');
    this.counterEl  = this.querySelector('[data-lightbox-counter]');
    this.prevBtn    = this.querySelector('[data-lightbox-prev]');
    this.nextBtn    = this.querySelector('[data-lightbox-next]');
    this.zoomInBtn  = this.querySelector('[data-lightbox-zoom-in]');
    this.zoomOutBtn = this.querySelector('[data-lightbox-zoom-out]');
    this.fsBtn      = this.querySelector('[data-lightbox-fullscreen]');
    this.closeEls   = this.querySelectorAll('[data-lightbox-close]');
    this.viewportEl = this.querySelector('[data-lightbox-viewport]');

    this.closeEls.forEach((b) => b.addEventListener('click', () => this.close()));
    if (this.prevBtn)    this.prevBtn.addEventListener('click', () => this.go(-1));
    if (this.nextBtn)    this.nextBtn.addEventListener('click', () => this.go(1));
    if (this.zoomInBtn)  this.zoomInBtn.addEventListener('click', () => this.zoomBy(1.5));
    if (this.zoomOutBtn) this.zoomOutBtn.addEventListener('click', () => this.zoomBy(1 / 1.5));
    if (this.fsBtn)      this.fsBtn.addEventListener('click', () => this.toggleFullscreen());

    if (this.viewportEl) {
      this.viewportEl.addEventListener('wheel', this.handleWheel, { passive: false });
      this.viewportEl.addEventListener('pointerdown', this.handlePointerDown);
    }

    document.addEventListener('mdj:gallery-zoom', this.handleZoomEvent);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('mdj:gallery-zoom', this.handleZoomEvent);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleZoomEvent(event) {
    const { images = [], startIndex = 0, fullscreen = false } = (event && event.detail) || {};
    this.show(images, startIndex);
    if (fullscreen) this.toggleFullscreen();
  }

  show(images, startIndex) {
    if (!images || !images.length) return;
    this.images = images;
    this.currentIndex = Math.max(0, Math.min(startIndex || 0, images.length - 1));
    this.renderThumbs();
    this.update();

    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('heritage-overlay-open');
  }

  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('heritage-overlay-open');
    this.exitFullscreen();
    this.resetTransform();
  }

  go(delta) {
    if (!this.images.length) return;
    const next = (this.currentIndex + delta + this.images.length) % this.images.length;
    this.currentIndex = next;
    this.resetTransform();
    this.update();
  }

  jumpTo(index) {
    if (index < 0 || index >= this.images.length) return;
    this.currentIndex = index;
    this.resetTransform();
    this.update();
  }

  zoomBy(factor, originX, originY) {
    const newScale = Math.max(1, Math.min(4, this.scale * factor));
    if (newScale === this.scale) return;

    if (typeof originX === 'number' && typeof originY === 'number' && this.viewportEl) {
      const rect = this.viewportEl.getBoundingClientRect();
      const cx = originX - rect.left - rect.width / 2;
      const cy = originY - rect.top - rect.height / 2;
      const ratio = newScale / this.scale;
      this.translateX = cx - (cx - this.translateX) * ratio;
      this.translateY = cy - (cy - this.translateY) * ratio;
    }
    this.scale = newScale;
    if (this.scale === 1) {
      this.translateX = 0;
      this.translateY = 0;
    }
    this.applyTransform();
  }

  resetTransform() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.applyTransform();
  }

  applyTransform() {
    if (!this.imageEl) return;
    this.imageEl.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    this.imageEl.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
  }

  update() {
    if (this.imageEl) {
      const src = this.images[this.currentIndex] || '';
      this.imageEl.style.backgroundImage = src ? `url('${src}')` : '';
    }
    if (this.counterEl) {
      this.counterEl.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }
    if (this.thumbsEl) {
      const buttons = this.thumbsEl.querySelectorAll('button');
      buttons.forEach((b, i) => {
        b.classList.toggle('is-active', i === this.currentIndex);
      });
    }
    if (this.prevBtn) this.prevBtn.disabled = this.images.length < 2;
    if (this.nextBtn) this.nextBtn.disabled = this.images.length < 2;
  }

  renderThumbs() {
    if (!this.thumbsEl) return;
    this.thumbsEl.innerHTML = '';
    if (this.images.length < 2) return;
    this.images.forEach((src, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'heritage-lightbox__thumb' + (i === this.currentIndex ? ' is-active' : '');
      btn.style.backgroundImage = `url('${src}')`;
      btn.setAttribute('aria-label', `View image ${i + 1}`);
      btn.addEventListener('click', () => this.jumpTo(i));
      this.thumbsEl.appendChild(btn);
    });
  }

  handleKeydown(event) {
    if (this.dataset.open !== 'true') return;
    if (event.key === 'Escape') this.close();
    else if (event.key === 'ArrowLeft') this.go(-1);
    else if (event.key === 'ArrowRight') this.go(1);
    else if (event.key === '+' || event.key === '=') this.zoomBy(1.5);
    else if (event.key === '-' || event.key === '_') this.zoomBy(1 / 1.5);
  }

  handleWheel(event) {
    if (this.dataset.open !== 'true') return;
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.zoomBy(factor, event.clientX, event.clientY);
  }

  handlePointerDown(event) {
    if (this.scale <= 1) return;
    this.isPanning = true;
    this.panStart = {
      x: event.clientX,
      y: event.clientY,
      tx: this.translateX,
      ty: this.translateY
    };
    if (this.imageEl) this.imageEl.style.cursor = 'grabbing';
    document.addEventListener('pointermove', this.handlePointerMove);
    document.addEventListener('pointerup', this.handlePointerUp, { once: true });
  }

  handlePointerMove(event) {
    if (!this.isPanning) return;
    this.translateX = this.panStart.tx + (event.clientX - this.panStart.x);
    this.translateY = this.panStart.ty + (event.clientY - this.panStart.y);
    this.applyTransform();
  }

  handlePointerUp() {
    this.isPanning = false;
    document.removeEventListener('pointermove', this.handlePointerMove);
    if (this.imageEl) this.imageEl.style.cursor = 'grab';
  }

  toggleFullscreen() {
    if (document.fullscreenElement) {
      this.exitFullscreen();
    } else if (this.requestFullscreen) {
      this.requestFullscreen().catch(() => {});
    }
  }

  exitFullscreen() {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  }
}

if (!customElements.get('heritage-gallery-lightbox')) {
  customElements.define('heritage-gallery-lightbox', HeritageGalleryLightbox);
}
